import React from 'react';
import { cn } from '@/lib/utils';

interface ReportMarkdownRendererProps {
  content: string;
  className?: string;
}

export const ReportMarkdownRenderer: React.FC<ReportMarkdownRendererProps> = ({ 
  content, 
  className = '' 
}) => {
  const renderMarkdown = (text: string) => {
    const lines = text.split('\n');
    const elements: React.ReactNode[] = [];
    let currentList: string[] = [];
    let listType: 'bullet' | 'numbered' | null = null;

    const flushList = () => {
      if (currentList.length > 0) {
        if (listType === 'numbered') {
          elements.push(
            <ol key={`ol-${elements.length}`} className="list-decimal list-inside space-y-1.5 my-3 ml-2">
              {currentList.map((item, i) => (
                <li key={i} className="text-sm text-foreground leading-relaxed pl-1">
                  {renderInlineFormatting(item)}
                </li>
              ))}
            </ol>
          );
        } else {
          elements.push(
            <ul key={`ul-${elements.length}`} className="list-disc list-inside space-y-1.5 my-3 ml-2">
              {currentList.map((item, i) => (
                <li key={i} className="text-sm text-foreground leading-relaxed pl-1">
                  {renderInlineFormatting(item)}
                </li>
              ))}
            </ul>
          );
        }
        currentList = [];
        listType = null;
      }
    };

    const renderInlineFormatting = (line: string): React.ReactNode => {
      const parts: React.ReactNode[] = [];
      let remaining = line;
      let partIndex = 0;

      // Process bold text **text** or __text__
      const boldRegex = /(\*\*|__)(.*?)\1/g;
      let lastIndex = 0;
      let match;

      while ((match = boldRegex.exec(remaining)) !== null) {
        if (match.index > lastIndex) {
          parts.push(
            <span key={partIndex++}>{remaining.slice(lastIndex, match.index)}</span>
          );
        }
        parts.push(
          <strong key={partIndex++} className="font-semibold text-foreground">
            {match[2]}
          </strong>
        );
        lastIndex = match.index + match[0].length;
      }

      if (lastIndex < remaining.length) {
        parts.push(<span key={partIndex++}>{remaining.slice(lastIndex)}</span>);
      }

      return parts.length > 0 ? parts : line;
    };

    lines.forEach((line, index) => {
      const trimmedLine = line.trim();

      // Empty line
      if (trimmedLine === '') {
        flushList();
        elements.push(<div key={`empty-${index}`} className="h-3" />);
        return;
      }

      // H1 Header: # Header
      if (trimmedLine.startsWith('# ')) {
        flushList();
        elements.push(
          <h1 key={index} className="text-2xl font-bold text-foreground mt-6 mb-3 pb-2 border-b border-border">
            {trimmedLine.slice(2)}
          </h1>
        );
        return;
      }

      // H2 Header: ## Header
      if (trimmedLine.startsWith('## ')) {
        flushList();
        elements.push(
          <h2 key={index} className="text-xl font-semibold text-foreground mt-5 mb-2">
            {trimmedLine.slice(3)}
          </h2>
        );
        return;
      }

      // H3 Header: ### Header
      if (trimmedLine.startsWith('### ')) {
        flushList();
        elements.push(
          <h3 key={index} className="text-lg font-semibold text-foreground mt-4 mb-2">
            {trimmedLine.slice(4)}
          </h3>
        );
        return;
      }

      // H4 Header: #### Header
      if (trimmedLine.startsWith('#### ')) {
        flushList();
        elements.push(
          <h4 key={index} className="text-base font-semibold text-foreground mt-3 mb-1">
            {trimmedLine.slice(5)}
          </h4>
        );
        return;
      }

      // Horizontal rule
      if (trimmedLine === '---' || trimmedLine === '***' || trimmedLine === '___') {
        flushList();
        elements.push(<hr key={index} className="my-4 border-border" />);
        return;
      }

      // Numbered list: 1. Item or 1) Item
      const numberedMatch = trimmedLine.match(/^\d+[\.\)]\s+(.*)$/);
      if (numberedMatch) {
        if (listType !== 'numbered') {
          flushList();
          listType = 'numbered';
        }
        currentList.push(numberedMatch[1]);
        return;
      }

      // Bullet list: - Item, * Item, or • Item
      const bulletMatch = trimmedLine.match(/^[-*•]\s+(.*)$/);
      if (bulletMatch) {
        if (listType !== 'bullet') {
          flushList();
          listType = 'bullet';
        }
        currentList.push(bulletMatch[1]);
        return;
      }

      // Regular paragraph
      flushList();
      elements.push(
        <p key={index} className="text-sm text-foreground leading-relaxed my-2">
          {renderInlineFormatting(trimmedLine)}
        </p>
      );
    });

    // Flush any remaining list items
    flushList();

    return elements;
  };

  return (
    <div className={cn("prose prose-sm max-w-none", className)}>
      {renderMarkdown(content)}
    </div>
  );
};
